/**
 * InternalSKUField Component
 * 
 * Text input for Internal SKU with debounced uniqueness validation.
 * Displays validation status: checking, valid, or duplicate.
 * 
 * Requirements: 3.1, 3.2
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { checkInternalSKU, type CheckInternalSKUResponse } from '../api/sku';

export type ValidationStatus = 'idle' | 'checking' | 'valid' | 'duplicate' | 'error';

export interface InternalSKUFieldProps {
  /** Current value */
  value: string;
  /** Called when value changes */
  onChange: (value: string) => void;
  /** Called when validation status changes */
  onValidationChange?: (status: ValidationStatus, result?: CheckInternalSKUResponse) => void;
  /** Debounce delay in milliseconds */
  debounceMs?: number;
  /** Whether the field is disabled */
  disabled?: boolean;
}

export function InternalSKUField({
  value,
  onChange,
  onValidationChange,
  debounceMs = 500,
  disabled = false,
}: InternalSKUFieldProps) {
  const [status, setStatus] = useState<ValidationStatus>('idle');
  const [duplicateInfo, setDuplicateInfo] = useState<CheckInternalSKUResponse | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Update status and notify parent
  const updateStatus = useCallback((newStatus: ValidationStatus, result?: CheckInternalSKUResponse) => {
    setStatus(newStatus);
    if (newStatus === 'duplicate' && result) {
      setDuplicateInfo(result);
    } else {
      setDuplicateInfo(null);
    }
    onValidationChange?.(newStatus, result);
  }, [onValidationChange]);

  // Validate the internal SKU
  const validateSKU = useCallback(async (sku: string) => {
    // Cancel any pending request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Skip validation for empty values
    if (!sku.trim()) {
      updateStatus('idle');
      return;
    }

    updateStatus('checking');
    abortControllerRef.current = new AbortController();

    try {
      const result = await checkInternalSKU(sku);
      
      if (result.isUnique) {
        updateStatus('valid');
      } else {
        updateStatus('duplicate', result);
      }
    } catch (err) {
      // Ignore abort errors
      if (err instanceof Error && err.name === 'AbortError') {
        return;
      }
      updateStatus('error');
    }
  }, [updateStatus]);

  // Debounced validation on value change
  useEffect(() => {
    // Clear existing timeout
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }

    // Set new timeout for validation
    debounceRef.current = setTimeout(() => {
      validateSKU(value);
    }, debounceMs);

    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [value, debounceMs, validateSKU]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Handle input change
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.value);
  };

  // Handle blur - trigger immediate validation
  const handleBlur = () => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    validateSKU(value);
  };

  // Get status indicator class and text
  const getStatusDisplay = () => {
    switch (status) {
      case 'checking':
        return { className: 'status-checking', text: 'Checking...' };
      case 'valid':
        return { className: 'status-valid', text: '✓ Available' };
      case 'duplicate':
        return { 
          className: 'status-duplicate', 
          text: duplicateInfo?.existingProductName 
            ? `✗ Already used by: ${duplicateInfo.existingProductName}`
            : '✗ Already exists'
        };
      case 'error':
        return { className: 'status-error', text: 'Validation failed' };
      default:
        return null;
    }
  };

  const statusDisplay = getStatusDisplay();

  return (
    <>
      <div className="internal-sku-input-wrapper">
        <input
          type="text"
          id="internalSku"
          value={value}
          onChange={handleChange}
          onBlur={handleBlur}
          placeholder="Enter internal SKU"
          disabled={disabled}
          aria-describedby={statusDisplay ? 'internalSkuStatus' : undefined}
          aria-invalid={status === 'duplicate'}
        />
        {statusDisplay && (
          <span 
            id="internalSkuStatus" 
            className={`internal-sku-status ${statusDisplay.className}`}
            role={status === 'duplicate' ? 'alert' : 'status'}
          >
            {statusDisplay.text}
          </span>
        )}
      </div>
    </>
  );
}

export default InternalSKUField;
